import React, { useCallback, useEffect, useState } from 'react';
import {
    Mail, MessageSquare, MousePointerClick, Users, UserPlus, Target,
    RefreshCw, X, Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchCampaignAttribution, fetchCampaignAttributionDetail,
} from '../../communications/api/communicationsApi';

/**
 * Campaign Leads — what the email and SMS we send actually produced.
 *
 * Every link that leaves the Communications Hub is stamped with
 * utm_source / utm_medium / utm_campaign (backend: communications/services/utm.py).
 * The buyer portal already records those tags on every event a visitor fires, so
 * this panel can follow one campaign all the way down:
 *
 *      sent → delivered → opened → clicked → visited → signed up → enquired
 *
 * The numbers only exist from the day tagging went live: a campaign sent before
 * that carries no tag and honestly reports zero leads rather than guessing.
 */

const NUM = new Intl.NumberFormat('en-AU');
const fmt = (v) => (v == null ? '—' : NUM.format(v));
const pct = (v) => (v == null ? '—' : `${v}%`);

const LEAD_EVENT_LABELS = {
    enquiry_sent: 'Property enquiries',
    inspection_requested: 'Inspection requests',
    offer_submitted: 'Offers submitted',
    bidder_registered: 'Bidder registrations',
    smsf_specialist_requested: 'SMSF specialist requests',
    mortgage_calculator_broker_clicked: 'Broker enquiries',
    alert_created: 'Property alerts created',
};

const labelFor = (event) =>
    LEAD_EVENT_LABELS[event] || event.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const ChannelIcon = ({ channel, className = 'w-4 h-4' }) =>
    channel === 'sms'
        ? <MessageSquare className={`${className} text-purple-500`} />
        : <Mail className={`${className} text-blue-500`} />;

const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: `${accent}1a`, color: accent }}>
                <Icon className="w-4 h-4" />
            </span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

// ── Who is behind one campaign's numbers ──────────────────────────────────
const LeadsModal = ({ campaignId, name, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        fetchCampaignAttributionDetail(campaignId)
            .then((res) => { if (alive) setData(res); })
            .catch(() => { if (alive) toast.error("Couldn't load this campaign's leads"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [campaignId]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
                    <div>
                        <h3 className="font-bold text-gray-900">{name}</h3>
                        <p className="text-xs text-gray-500">
                            People who acted after clicking this campaign
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading && <p className="text-gray-400 text-sm p-5">Loading…</p>}

                {!loading && data && (
                    <div className="p-5 space-y-5">
                        <div>
                            <h4 className="font-semibold text-gray-800 text-sm mb-2">
                                Leads ({data.leads.length})
                            </h4>
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                {data.leads.map((l, i) => (
                                    <div key={i} className="flex justify-between items-center px-3 py-2 text-sm">
                                        <span className="text-gray-700">
                                            {l.name || l.email || (
                                                <span className="italic text-gray-400">
                                                    Not signed in yet
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-gray-400 text-xs">
                                            {labelFor(l.event)} · {new Date(l.occurred_at).toLocaleDateString('en-AU')}
                                        </span>
                                    </div>
                                ))}
                                {data.leads.length === 0 && (
                                    <p className="text-gray-400 text-sm px-3 py-2">
                                        No leads from this campaign yet.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 text-sm mb-2">
                                New portal sign-ups ({data.signups.length})
                            </h4>
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                                {data.signups.map((s, i) => (
                                    <div key={i} className="flex justify-between items-center px-3 py-2 text-sm">
                                        <span className="text-gray-700">{s.name || s.email}</span>
                                        <span className="text-gray-400 text-xs">
                                            {new Date(s.joined_at).toLocaleDateString('en-AU')}
                                        </span>
                                    </div>
                                ))}
                                {data.signups.length === 0 && (
                                    <p className="text-gray-400 text-sm px-3 py-2">
                                        No sign-ups from this campaign yet.
                                    </p>
                                )}
                            </div>
                        </div>

                        <p className="text-xs text-gray-400">
                            Tracking tag: <code>{data.campaign.utm_campaign}</code>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── The panel ─────────────────────────────────────────────────────────────
export default function CampaignLeadsPanel({ startDate, endDate }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);   // { id, name } of the row being drilled into
    const [showHow, setShowHow] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        return fetchCampaignAttribution(params)
            .then(setData)
            .catch(() => toast.error("Couldn't load campaign leads"))
            .finally(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <p className="text-gray-400 text-sm p-6">Loading…</p>;
    if (!data) return <p className="text-gray-400 text-sm p-6">No data.</p>;

    const t = data.totals;
    const rows = data.rows || [];
    const anyAttribution = t.visits > 0 || t.leads > 0 || t.signups > 0;

    return (
        <div>
            {!data.send_enabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                    <strong>Sending is OFF (safe mode)</strong> — campaigns are simulated, so
                    nothing below reflects real delivery yet.
                </div>
            )}

            <div className="flex items-center justify-between mb-4">
                <button onClick={() => setShowHow((v) => !v)}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                    <Info className="w-4 h-4" />
                    How these numbers are worked out
                </button>
                <button onClick={load} disabled={loading}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {showHow && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-600 space-y-2">
                    <p>
                        Every link in an email or SMS sent from the Communications Hub carries a
                        tracking tag. When someone clicks it and lands on the buyer portal, the tag
                        travels with them, so anything they do next is credited to the campaign
                        that brought them.
                    </p>
                    <p>
                        <strong>Leads</strong> are actions with real intent — property enquiries,
                        inspection requests, offers, bidder registrations, broker and SMSF
                        enquiries, and property alerts. Browsing is counted separately as Visits,
                        not as a lead.
                    </p>
                    <p>
                        <strong>Sign-ups</strong> are credited on first touch: whichever campaign
                        first brought that person in keeps the credit, which is how the Traffic
                        Sources and Cost Per Lead tabs count them too.
                    </p>
                    <p className="text-gray-500">
                        Campaigns sent before link tracking was switched on carry no tag and
                        correctly show zero, rather than a guess.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard Icon={Mail} label="Sent" value={fmt(t.sent)}
                          sub={`${fmt(t.campaigns)} campaigns`} accent="#3b82f6" />
                <StatCard Icon={MousePointerClick} label="Clicked" value={fmt(t.clicked)}
                          sub={`${pct(t.click_rate)} click rate`} accent="#8b5cf6" />
                <StatCard Icon={Users} label="Portal visits" value={fmt(t.visits)}
                          sub={`${fmt(t.sessions)} sessions`} accent="#0ea5e9" />
                <StatCard Icon={UserPlus} label="Sign-ups" value={fmt(t.signups)}
                          sub="new portal accounts" accent="#14b8a6" />
                <StatCard Icon={Target} label="Leads" value={fmt(t.leads)}
                          sub={`${pct(t.lead_rate)} of delivered`} accent="#f97316" />
                <StatCard Icon={Target} label="Leads per 1,000" value={fmt(t.leads_per_1k)}
                          sub="comparable across list sizes" accent="#ef4444" />
            </div>

            {!anyAttribution && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-sm text-blue-900">
                    <strong>No attributed activity yet.</strong> Link tracking applies to campaigns
                    sent from now on — send a tagged campaign and the visits, sign-ups and leads it
                    produces will appear here.
                </div>
            )}

            {(data.by_channel || []).length > 0 && (
                <>
                    <h3 className="font-bold text-gray-800 mt-6 mb-2">Email vs SMS</h3>
                    <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                        {data.by_channel.map((c) => (
                            <div key={c.channel} className="flex justify-between items-center px-4 py-3 text-sm">
                                <span className="flex items-center gap-2 font-medium text-gray-700 capitalize">
                                    <ChannelIcon channel={c.channel} />
                                    {c.channel === 'sms' ? 'SMS' : c.channel}
                                </span>
                                <span className="text-gray-500">
                                    {fmt(c.sent)} sent · {fmt(c.visits)} visits · {fmt(c.signups)} sign-ups ·{' '}
                                    <strong className="text-gray-800">{fmt(c.leads)} leads</strong>
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {(data.lead_types || []).length > 0 && (
                <>
                    <h3 className="font-bold text-gray-800 mt-6 mb-2">What the leads were</h3>
                    <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                        {data.lead_types.map((l) => (
                            <div key={l.event} className="flex justify-between items-center px-4 py-3 text-sm">
                                <span className="text-gray-700">{labelFor(l.event)}</span>
                                <span className="font-semibold text-gray-900">{fmt(l.count)}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <h3 className="font-bold text-gray-800 mt-6 mb-2">Every campaign</h3>
            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                            <th className="px-4 py-2.5 font-medium">Campaign</th>
                            <th className="px-3 py-2.5 font-medium">Sent</th>
                            <th className="px-3 py-2.5 font-medium">Delivered</th>
                            <th className="px-3 py-2.5 font-medium">Opened</th>
                            <th className="px-3 py-2.5 font-medium">Clicked</th>
                            <th className="px-3 py-2.5 font-medium">Visits</th>
                            <th className="px-3 py-2.5 font-medium">Sign-ups</th>
                            <th className="px-3 py-2.5 font-medium">Leads</th>
                            <th className="px-3 py-2.5 font-medium">Per 1k</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {rows.map((r) => (
                            <tr key={r.campaign_id || r.utm_campaign}
                                onClick={() => r.campaign_id && setOpen({ id: r.campaign_id, name: r.name })}
                                className={r.campaign_id ? 'hover:bg-gray-50 cursor-pointer' : ''}>
                                <td className="px-4 py-2.5">
                                    <span className="flex items-center gap-2 font-medium text-gray-800">
                                        <ChannelIcon channel={r.channel} />
                                        {r.name}
                                        {!r.campaign_id && (
                                            /* Not a Hub campaign — the digest, portal
                                               invites and listing alerts that send
                                               themselves. Worth showing, but they have
                                               no send date or owner to drill into. */
                                            <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">
                                                Automated
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                        {r.agent && `${r.agent} · `}
                                        {r.sent_at
                                            ? new Date(r.sent_at).toLocaleDateString('en-AU')
                                            : 'ongoing'}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.sent)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.delivered)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.opened)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.clicked)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.visits)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.signups)}</td>
                                <td className="px-3 py-2.5 font-semibold text-gray-900">{fmt(r.leads)}</td>
                                <td className="px-3 py-2.5 text-gray-600">{fmt(r.leads_per_1k)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-4 py-4 text-gray-400">
                                    No campaigns have been sent yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">
                Click a campaign to see the people behind its numbers.
            </p>

            {open && (
                <LeadsModal campaignId={open.id} name={open.name} onClose={() => setOpen(null)} />
            )}
        </div>
    );
}
